import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { spouseService } from '../../../../services/spouse';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('SpouseInfoInput');

type RouteParams = {
  taskId?: number;
  spouseData?: any;
  isEditing?: boolean;
};

export const SpouseInfoInputScreen: React.FC = () => {
  const navigation = useNavigation();
  const route = useRoute<RouteProp<Record<string, RouteParams>, string>>();
  const taskId = route.params?.taskId || 3; // Spouse task ID is 3
  const paramSpouseData = route.params?.spouseData;
  const paramIsEditing = route.params?.isEditing || false;

  // State for showing/hiding sensitive fields
  const [showSSN, setShowSSN] = useState(false);
  const [showDriversLicense, setShowDriversLicense] = useState(false);
  const [showPassportNumber, setShowPassportNumber] = useState(false);

  logger.debug('[SpouseInput Debug] Route params:', route.params);
  logger.debug('[SpouseInput Debug] paramSpouseData:', paramSpouseData);
  logger.debug('[SpouseInput Debug] paramIsEditing:', paramIsEditing);

  // Helper function to format phone number as (xxx) xxx-xxxx
  const formatPhoneNumber = (text: string) => {
    // Remove all non-numeric characters
    const cleaned = text.replace(/\D/g, '');

    // Format based on length
    if (cleaned.length === 0) return '';
    if (cleaned.length <= 3) return `(${cleaned}`;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    if (cleaned.length <= 10) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;

    // Limit to 10 digits
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  // Helper function to format SSN as xxx-xx-xxxx
  const formatSSN = (text: string) => {
    // Remove all non-numeric characters
    const cleaned = text.replace(/\D/g, '');

    // Format based on length
    if (cleaned.length === 0) return '';
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 5) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    if (cleaned.length <= 9) return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5)}`;

    // Limit to 9 digits
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5, 9)}`;
  };

  // Helper function to validate email format
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Helper function to check if all required emails are valid
  const areEmailsValid = (): boolean => {
    const emails = [formData.email, formData.workEmail];
    return emails.every(email => email === '' || validateEmail(email));
  };

  // Helper function to format date as MM/DD/YYYY
  const formatDate = (text: string) => {
    // Remove all non-numeric characters
    const cleaned = text.replace(/\D/g, '');
    
    // Format based on length
    if (cleaned.length === 0) return '';
    if (cleaned.length <= 2) return cleaned;
    if (cleaned.length <= 4) return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
    if (cleaned.length <= 8) return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4)}`;
    
    // Limit to 8 digits (MMDDYYYY)
    return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4, 8)}`;
  };

  // Helper function to convert ISO date (YYYY-MM-DD) to input format (MM/DD/YYYY)
  const convertISOToInputFormat = (isoDate: string): string => {
    if (!isoDate) return '';

    // Handle both Date objects and ISO strings
    const dateStr = typeof isoDate === 'string' ? isoDate : isoDate.toString();

    // Check if it's already in MM/DD/YYYY format
    if (dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      return dateStr;
    }

    // Parse ISO format (YYYY-MM-DD or full ISO timestamp)
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';

    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();

    return `${month}/${day}/${year}`;
  };

  // Helper function to validate date format MM/DD/YYYY
  const validateDate = (date: string): boolean => {
    if (!date || date.length !== 10) return false;

    const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
    if (!dateRegex.test(date)) return false;

    // Extract month, day, and year
    const [month, day, year] = date.split('/').map(Number);
    
    // Check if year is reasonable (1900 to current year + 1)
    const currentYear = new Date().getFullYear();
    if (year < 1900 || year > currentYear + 1) return false;
    
    // Check if month and day are valid
    const dateObj = new Date(year, month - 1, day);
    return dateObj.getMonth() === month - 1 && dateObj.getDate() === day;
  };

  // Helper function to check if all required dates are valid
  const areDatesValid = (): boolean => {
    const dates = [formData.dateOfBirth, formData.marriageDate];
    return dates.every(date => date === '' || validateDate(date));
  };

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    maidenName: '',
    dateOfBirth: '',
    phoneNumber: '',
    email: '',
    occupation: '',
    employer: '',
    workPhone: '',
    workEmail: '',
    workAddress: '',
    ssn: '',
    driversLicense: '',
    passportNumber: '',
    marriageDate: '',
    marriageLocation: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelationship: '',
    medicalConditions: '',
    allergies: '',
    medications: '',
    additionalNotes: '',
  });

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [existingSpouse, setExistingSpouse] = useState<any>(null);

  // Load existing spouse information
  useEffect(() => {
    loadSpouseInfo();
  }, [taskId, paramSpouseData]);

  const loadSpouseInfo = async () => {
    try {
      setInitialLoading(true);

      logger.debug('[SpouseInput Debug] loadSpouseInfo - paramSpouseData:', paramSpouseData);
      logger.debug('[SpouseInput Debug] loadSpouseInfo - paramIsEditing:', paramIsEditing);

      // If spouse data was passed via navigation params, use it directly
      if (paramSpouseData && paramIsEditing) {
        logger.debug('[SpouseInput Debug] Using passed spouse data');
        const spouse = paramSpouseData;
        setExistingSpouse(spouse);
        setIsEditing(true);

        // Load regular spouse data
        let spouseFormData = {
          firstName: spouse.firstName || '',
          lastName: spouse.lastName || '',
          maidenName: spouse.maidenName || '',
          dateOfBirth: convertISOToInputFormat(spouse.dateOfBirth) || '',
          phoneNumber: spouse.phoneNumber || '',
          email: spouse.email || '',
          occupation: spouse.occupation || '',
          employer: spouse.employer || '',
          workPhone: spouse.workPhone || '',
          workEmail: spouse.workEmail || '',
          workAddress: spouse.workAddress || '',
          ssn: '',
          driversLicense: '',
          passportNumber: '',
          marriageDate: convertISOToInputFormat(spouse.marriageDate) || '',
          marriageLocation: spouse.marriageLocation || '',
          emergencyContactName: spouse.emergencyContactName || '',
          emergencyContactPhone: spouse.emergencyContactPhone || '',
          emergencyContactRelationship: spouse.emergencyContactRelationship || '',
          medicalConditions: spouse.medicalConditions || '',
          allergies: spouse.allergies || '',
          medications: spouse.medications || '',
          additionalNotes: spouse.additionalNotes || '',
        };

        // Load sensitive data from vault
        try {
          const sensitiveData = await spouseService.getSpouseCredentials(spouse.id);
          spouseFormData.ssn = sensitiveData.ssn || '';
          spouseFormData.driversLicense = sensitiveData.driversLicense || '';
          spouseFormData.passportNumber = sensitiveData.passportNumber || '';
        } catch (error) {
          logger.warn('Could not load sensitive data from vault:', error);
          // Continue with empty sensitive fields if vault data is not available
        }

        setFormData(spouseFormData);
        logger.debug('[SpouseInput Debug] Form data set from params:', spouseFormData);
        setInitialLoading(false);
        return;
      }

      // Otherwise, load from API
      logger.debug('[SpouseInput Debug] Loading from API with taskId:', taskId);
      const data = await spouseService.getSpouse(taskId);
      if (data && data.length > 0) {
        const spouse = data[0];
        setExistingSpouse(spouse);
        setIsEditing(true);

        // Load regular spouse data
        let spouseFormData = {
          firstName: spouse.firstName || '',
          lastName: spouse.lastName || '',
          maidenName: spouse.maidenName || '',
          dateOfBirth: convertISOToInputFormat(spouse.dateOfBirth) || '',
          phoneNumber: spouse.phoneNumber || '',
          email: spouse.email || '',
          occupation: spouse.occupation || '',
          employer: spouse.employer || '',
          workPhone: spouse.workPhone || '',
          workEmail: spouse.workEmail || '',
          workAddress: spouse.workAddress || '',
          ssn: '',
          driversLicense: '',
          passportNumber: '',
          marriageDate: convertISOToInputFormat(spouse.marriageDate) || '',
          marriageLocation: spouse.marriageLocation || '',
          emergencyContactName: spouse.emergencyContactName || '',
          emergencyContactPhone: spouse.emergencyContactPhone || '',
          emergencyContactRelationship: spouse.emergencyContactRelationship || '',
          medicalConditions: spouse.medicalConditions || '',
          allergies: spouse.allergies || '',
          medications: spouse.medications || '',
          additionalNotes: spouse.additionalNotes || '',
        };

        // Load sensitive data from vault
        try {
          const sensitiveData = await spouseService.getSpouseCredentials(spouse.id);
          spouseFormData.ssn = sensitiveData.ssn || '';
          spouseFormData.driversLicense = sensitiveData.driversLicense || '';
          spouseFormData.passportNumber = sensitiveData.passportNumber || '';
        } catch (error) {
          logger.warn('Could not load sensitive data from vault:', error);
          // Continue with empty sensitive fields if vault data is not available
        }

        setFormData(spouseFormData);
      }
    } catch (error) {
      logger.error('Error loading spouse info:', error);
      // Don't show alert for missing data - just means no data exists yet
    } finally {
      setInitialLoading(false);
    }
  };

  const saveSpouseInfo = async () => {
    try {
      setLoading(true);
      
      // Validate emails before saving
      if (!areEmailsValid()) {
        Alert.alert('Invalid Email', 'Please check that all email addresses are in the correct format (e.g., user@example.com)');
        setLoading(false);
        return;
      }

      // Validate dates before saving
      if (!areDatesValid()) {
        Alert.alert('Invalid Date', 'Please check that all dates are in the correct format (MM/DD/YYYY) and are valid dates');
        setLoading(false);
        return;
      }
      
      // Separate sensitive data from regular spouse data
      const spouseData = {
        taskId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        maidenName: formData.maidenName,
        dateOfBirth: formData.dateOfBirth,
        phoneNumber: formData.phoneNumber,
        email: formData.email,
        occupation: formData.occupation,
        employer: formData.employer,
        workPhone: formData.workPhone,
        workEmail: formData.workEmail,
        workAddress: formData.workAddress,
        marriageDate: formData.marriageDate,
        marriageLocation: formData.marriageLocation,
        emergencyContactName: formData.emergencyContactName,
        emergencyContactPhone: formData.emergencyContactPhone,
        emergencyContactRelationship: formData.emergencyContactRelationship,
        medicalConditions: formData.medicalConditions,
        allergies: formData.allergies,
        medications: formData.medications,
        additionalNotes: formData.additionalNotes,
      };

      // Sensitive data for vault storage
      const sensitiveData = {
        ssn: formData.ssn,
        driversLicense: formData.driversLicense,
        passportNumber: formData.passportNumber,
      };

      let spouseId: string;
      
      if (isEditing && existingSpouse) {
        // Update existing record
        await spouseService.updateSpouse(existingSpouse.id, spouseData);
        spouseId = existingSpouse.id;
      } else {
        // Create new record
        const createResponse = await spouseService.createSpouse(spouseData);
        // Handle wrapped response format { success, data, message }
        const createdData = createResponse?.data || createResponse;
        spouseId = createdData.id;
      }

      // Store or update sensitive data in vault if any sensitive fields have values
      if (sensitiveData.ssn || sensitiveData.driversLicense || sensitiveData.passportNumber) {
        await spouseService.storeSpouseCredentials(spouseId, sensitiveData);
      }
      
      Alert.alert('Success', 'Spouse information saved successfully!', [
        { text: 'OK', onPress: () => navigation.navigate('SpouseInfoLockUnlock', { spouseId }) },
      ]);
    } catch (error) {
      logger.error('Error saving spouse info:', error);
      Alert.alert('Error', 'Failed to save spouse information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    saveSpouseInfo();
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>Add Spouse Information</Text>
      </View>

      {initialLoading ? (
        <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
          <ActivityIndicator size="large" color="#29BFC0" />
          <Text style={{ marginTop: 10, color: '#666' }}>Loading spouse information...</Text>
        </View>
      ) : (
        <View style={styles.inputContainer}>
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollViewContent}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
            automaticallyAdjustKeyboardInsets={true}
          >
            <View style={styles.inputGroup}>
              <Text style={styles.label}>First Name</Text>
              <TextInput
                style={styles.input}
                value={formData.firstName}
                onChangeText={(text) => setFormData({ ...formData, firstName: text })}
                placeholder="First Name"
                placeholderTextColor="#9CA3AF"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Last Name</Text>
              <TextInput
                style={styles.input}
                value={formData.lastName}
                onChangeText={(text) => setFormData({ ...formData, lastName: text })}
                placeholder="Last Name"
                placeholderTextColor="#9CA3AF"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Maiden Name</Text>
              <TextInput
                style={styles.input}
                value={formData.maidenName}
                onChangeText={(text) => setFormData({ ...formData, maidenName: text })}
                placeholder="Maiden Name"
                placeholderTextColor="#9CA3AF"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Date of Birth</Text>
              <TextInput
                style={[
                  styles.input, 
                  formData.dateOfBirth && !validateDate(formData.dateOfBirth) && styles.inputError
                ]}
                value={formData.dateOfBirth}
                onChangeText={(text) => setFormData({ ...formData, dateOfBirth: formatDate(text) })}
                placeholder="MM/DD/YYYY"
                placeholderTextColor="#9CA3AF"
                keyboardType="numeric"
                maxLength={10}
              />
              {formData.dateOfBirth && !validateDate(formData.dateOfBirth) && (
                <Text style={styles.errorText}>Please enter a valid date (MM/DD/YYYY)</Text>
              )}
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Phone Number</Text>
              <TextInput
                style={styles.input}
                value={formData.phoneNumber}
                onChangeText={(text) => setFormData({ ...formData, phoneNumber: formatPhoneNumber(text) })}
                placeholder="(555) 123-4567"
                placeholderTextColor="#9CA3AF"
                keyboardType="phone-pad"
                maxLength={14}
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Email</Text>
              <TextInput
                style={[
                  styles.input, 
                  formData.email && !validateEmail(formData.email) && styles.inputError
                ]}
                value={formData.email}
                onChangeText={(text) => setFormData({ ...formData, email: text })}
                placeholder="user@example.com"
                placeholderTextColor="#9CA3AF"
                keyboardType="email-address"
                autoCapitalize="none"
              />
              {formData.email && !validateEmail(formData.email) && (
                <Text style={styles.errorText}>Please enter a valid email address</Text>
              )}
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Occupation</Text>
              <TextInput
                style={styles.input}
                value={formData.occupation}
                onChangeText={(text) => setFormData({ ...formData, occupation: text })}
                placeholder="Occupation"
                placeholderTextColor="#9CA3AF"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Employer</Text>
              <TextInput
                style={styles.input}
                value={formData.employer}
                onChangeText={(text) => setFormData({ ...formData, employer: text })}
                placeholder="Employer"
                placeholderTextColor="#9CA3AF"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Work Phone</Text>
              <TextInput
                style={styles.input}
                value={formData.workPhone}
                onChangeText={(text) => setFormData({ ...formData, workPhone: formatPhoneNumber(text) })}
                placeholder="(555) 123-4567"
                placeholderTextColor="#9CA3AF"
                keyboardType="phone-pad"
                maxLength={14}
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Work Email</Text>
              <TextInput
                style={[
                  styles.input, 
                  formData.workEmail && !validateEmail(formData.workEmail) && styles.inputError
                ]}
                value={formData.workEmail}
                onChangeText={(text) => setFormData({ ...formData, workEmail: text })}
                placeholder="work@company.com"
                placeholderTextColor="#9CA3AF"
                keyboardType="email-address"
                autoCapitalize="none"
              />
              {formData.workEmail && !validateEmail(formData.workEmail) && (
                <Text style={styles.errorText}>Please enter a valid email address</Text>
              )}
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Work Address</Text>
              <TextInput
                style={styles.input}
                value={formData.workAddress}
                onChangeText={(text) => setFormData({ ...formData, workAddress: text })}
                placeholder="Work Address"
                placeholderTextColor="#9CA3AF"
                multiline={true}
                numberOfLines={2}
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>SSN 🔒</Text>
              <View style={styles.inputWithIcon}>
                <TextInput
                  style={[styles.input, styles.inputWithEye]}
                  value={formData.ssn}
                  onChangeText={(text) => setFormData({ ...formData, ssn: formatSSN(text) })}
                  placeholder="XXX-XX-XXXX"
                  placeholderTextColor="#9CA3AF"
                  secureTextEntry={!showSSN}
                  autoCapitalize="none"
                  autoCorrect={false}
                  keyboardType="numeric"
                  maxLength={11}
                />
                <TouchableOpacity
                  style={styles.eyeIcon}
                  onPress={() => setShowSSN(!showSSN)}
                >
                  <Image
                    source={showSSN ?
                      require('../../../../assets/icons/Eye.png') :
                      require('../../../../assets/icons/Eye-closed.png')
                    }
                    style={styles.eyeIconImage}
                    resizeMode="contain"
                  />
                </TouchableOpacity>
              </View>
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Driver's License 🔒</Text>
              <View style={styles.inputWithIcon}>
                <TextInput
                  style={[styles.input, styles.inputWithEye]}
                  value={formData.driversLicense}
                  onChangeText={(text) => setFormData({ ...formData, driversLicense: text })}
                  placeholder="Driver's License Number"
                  placeholderTextColor="#9CA3AF"
                  secureTextEntry={!showDriversLicense}
                  autoCapitalize="none"
                  autoCorrect={false}
                />
                <TouchableOpacity
                  style={styles.eyeIcon}
                  onPress={() => setShowDriversLicense(!showDriversLicense)}
                >
                  <Image
                    source={showDriversLicense ?
                      require('../../../../assets/icons/Eye.png') :
                      require('../../../../assets/icons/Eye-closed.png')
                    }
                    style={styles.eyeIconImage}
                    resizeMode="contain"
                  />
                </TouchableOpacity>
              </View>
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Passport Number 🔒</Text>
              <View style={styles.inputWithIcon}>
                <TextInput
                  style={[styles.input, styles.inputWithEye]}
                  value={formData.passportNumber}
                  onChangeText={(text) => setFormData({ ...formData, passportNumber: text })}
                  placeholder="Passport Number"
                  placeholderTextColor="#9CA3AF"
                  secureTextEntry={!showPassportNumber}
                  autoCapitalize="none"
                  autoCorrect={false}
                />
                <TouchableOpacity
                  style={styles.eyeIcon}
                  onPress={() => setShowPassportNumber(!showPassportNumber)}
                >
                  <Image
                    source={showPassportNumber ?
                      require('../../../../assets/icons/Eye.png') :
                      require('../../../../assets/icons/Eye-closed.png')
                    }
                    style={styles.eyeIconImage}
                    resizeMode="contain"
                  />
                </TouchableOpacity>
              </View>
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Marriage Date</Text>
              <TextInput
                style={[
                  styles.input, 
                  formData.marriageDate && !validateDate(formData.marriageDate) && styles.inputError
                ]}
                value={formData.marriageDate}
                onChangeText={(text) => setFormData({ ...formData, marriageDate: formatDate(text) })}
                placeholder="MM/DD/YYYY"
                placeholderTextColor="#9CA3AF"
                keyboardType="numeric"
                maxLength={10}
              />
              {formData.marriageDate && !validateDate(formData.marriageDate) && (
                <Text style={styles.errorText}>Please enter a valid date (MM/DD/YYYY)</Text>
              )}
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Marriage Location</Text>
              <TextInput
                style={styles.input}
                value={formData.marriageLocation}
                onChangeText={(text) => setFormData({ ...formData, marriageLocation: text })}
                placeholder="City, State"
                placeholderTextColor="#9CA3AF"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Emergency Contact Name</Text>
              <TextInput
                style={styles.input}
                value={formData.emergencyContactName}
                onChangeText={(text) => setFormData({ ...formData, emergencyContactName: text })}
                placeholder="Emergency Contact Name"
                placeholderTextColor="#9CA3AF"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Emergency Contact Phone</Text>
              <TextInput
                style={styles.input}
                value={formData.emergencyContactPhone}
                onChangeText={(text) => setFormData({ ...formData, emergencyContactPhone: formatPhoneNumber(text) })}
                placeholder="(555) 123-4567"
                placeholderTextColor="#9CA3AF"
                keyboardType="phone-pad"
                maxLength={14}
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Emergency Contact Relationship</Text>
              <TextInput
                style={styles.input}
                value={formData.emergencyContactRelationship}
                onChangeText={(text) => setFormData({ ...formData, emergencyContactRelationship: text })}
                placeholder="Relationship"
                placeholderTextColor="#9CA3AF"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Medical Conditions</Text>
              <TextInput
                style={[styles.input, styles.notesInput]}
                value={formData.medicalConditions}
                onChangeText={(text) => setFormData({ ...formData, medicalConditions: text })}
                placeholder="Any medical conditions or health issues"
                placeholderTextColor="#9CA3AF"
                multiline={true}
                numberOfLines={4}
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Allergies</Text>
              <TextInput
                style={[styles.input, styles.notesInput]}
                value={formData.allergies}
                onChangeText={(text) => setFormData({ ...formData, allergies: text })}
                placeholder="Any allergies"
                placeholderTextColor="#9CA3AF"
                multiline={true}
                numberOfLines={4}
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Medications</Text>
              <TextInput
                style={[styles.input, styles.notesInput]}
                value={formData.medications}
                onChangeText={(text) => setFormData({ ...formData, medications: text })}
                placeholder="Current medications"
                placeholderTextColor="#9CA3AF"
                multiline={true}
                numberOfLines={4}
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Additional Notes</Text>
              <TextInput
                style={[styles.input, styles.notesInput]}
                value={formData.additionalNotes}
                onChangeText={(text) => setFormData({ ...formData, additionalNotes: text })}
                placeholder="Any additional spouse information"
                placeholderTextColor="#9CA3AF"
                multiline={true}
                numberOfLines={4}
              />
            </View>

            <TouchableOpacity 
              style={[styles.submitButton, loading && styles.submitButtonDisabled]}
              onPress={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text style={styles.submitButtonText}>
                  {isEditing ? 'Update Spouse Info' : 'Save Spouse Info'}
                </Text>
              )}
            </TouchableOpacity>
          </ScrollView>
        </View>
      )}

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    marginBottom: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(10),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    textAlign: 'center',
    marginHorizontal: ms(20),
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(30),
    paddingHorizontal: ms(20),
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(30),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
    placeholderTextColor: '#E5E5E5',
  },
  inputWithIcon: {
    position: 'relative',
  },
  inputWithEye: {
    paddingRight: ms(50),
  },
  eyeIcon: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -10 }],
    padding: ms(5),
    zIndex: 1,
  },
  eyeIconImage: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
  },
  notesInput: {
    height: s(100),
    textAlignVertical: 'top',
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(130),
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  submitButtonDisabled: {
    backgroundColor: '#999',
    opacity: 0.6,
  },
  inputError: {
    borderColor: '#FF4444',
    borderWidth: ms(2),
  },
  errorText: {
    color: '#FF4444',
    fontSize: ms(14, 0.3),
    marginTop: ms(5),
    marginLeft: ms(15),
  },
}); 